import React, { useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Button,
  Typography,
  Popover,
  Input,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChromePicker } from "react-color";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import VehiclesSection from "./VehiclesSection";
import StopsSection from "./StopsSection";
import RouteSelection from "./RouteSelection";
import SubmitButton from "./SubmitButton";
import RouteAddMap from "./RouteAddMap";
import axios from "axios";
import config from "../../config";
import {
  buildScheduleConflictMessage,
  getApiErrorResponse,
  getGenericApiErrorMessage,
} from "../../utils/scheduleUi";

export default function RouteAddDetails({
  routeData,
  onRouteDataChange,
  stops,
  selectedStops,
  onStopSelect,
  vehicles,
  onVehicleSelect,
  onSubmit,
  errors,
  routeType,
  onRouteTypeChange,
  onRouteSchedulesClick,
  onAssignSchedulesClick,
  isUpdateMode,
  initialTripData,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const vehicleList = Array.isArray(vehicles)
    ? vehicles
    : vehicles.vehicles || [];

  const [anchorEl, setAnchorEl] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [tripData, setTripData] = useState({
    tripDate: null,
    startTime: null,
    endTime: null,
  });
  const existingOneTimeSchedule =
    initialTripData && Array.isArray(routeData?.schedules)
      ? routeData.schedules.find((schedule) => schedule.repitation === "ONETIME") ||
        routeData.schedules[0]
      : null;

  const handleColorPickerOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setAnchorEl(null);
  };

  const handleInputChange = (field, value) => {
    console.log(field, value, "field,value");
    setSubmitError("");
    onRouteDataChange(field, value);
  };

  const handleTripDataChange = (newTripData) => {
    setSubmitError("");
    setTripData(newTripData);
  };

  const getOrderedRouteStops = () => {
    const orderedStops = [];

    const pushStop = (stop) => {
      if (!stop?.id) return;

      const normalizedStop = {
        stopId: Number(stop.id),
        stopName: stop.name || "",
      };
      const previousStop = orderedStops[orderedStops.length - 1];

      if (!previousStop || previousStop.stopId !== normalizedStop.stopId) {
        orderedStops.push(normalizedStop);
      }
    };

    pushStop(routeData.origin);

    (routeData.routePoints || []).forEach((point) => {
      if (point?.isStop && point?.stopId) {
        pushStop({ id: point.stopId, name: point.name });
      }
    });

    pushStop(routeData.destination);

    return orderedStops;
  };

  const buildStopScheduleTimeline = (orderedStops, startDateTime, endDateTime) => {
    if (!orderedStops.length) {
      return [];
    }

    const startLabel = startDateTime.format("HH:mm");
    const endLabel = endDateTime.format("HH:mm");

    if (orderedStops.length === 1) {
      return [
        {
          stopId: orderedStops[0].stopId,
          arrivalTime: startLabel,
          departureTime: startLabel,
        },
      ];
    }

    const totalMinutes = Math.max(0, endDateTime.diff(startDateTime, "minute"));

    if (orderedStops.length === 2) {
      return [
        {
          stopId: orderedStops[0].stopId,
          arrivalTime: startLabel,
          departureTime: startLabel,
        },
        {
          stopId: orderedStops[1].stopId,
          arrivalTime: endLabel,
          departureTime: endLabel,
        },
      ];
    }

    const totalSegments = orderedStops.length * 2 - 3;
    const canCreateStrictTimeline = totalMinutes >= totalSegments;

    const offsets = canCreateStrictTimeline
      ? (() => {
          const strictOffsets = [0];

          for (let index = 1; index < totalSegments; index += 1) {
            const idealOffset = Math.round((totalMinutes * index) / totalSegments);
            const minOffset = strictOffsets[index - 1] + 1;
            const maxOffset = totalMinutes - (totalSegments - index);

            strictOffsets.push(Math.min(Math.max(idealOffset, minOffset), maxOffset));
          }

          strictOffsets.push(totalMinutes);
          return strictOffsets;
        })()
      : Array.from({ length: totalSegments + 1 }, (_, index) =>
          Math.round((totalMinutes * index) / totalSegments),
        );

    return orderedStops.map((stop, index) => {
      if (index === 0) {
        return {
          stopId: stop.stopId,
          arrivalTime: startLabel,
          departureTime: startLabel,
        };
      }

      if (index === orderedStops.length - 1) {
        return {
          stopId: stop.stopId,
          arrivalTime: endLabel,
          departureTime: endLabel,
        };
      }

      const arrivalOffset = offsets[index * 2 - 1];
      const departureOffset = offsets[index * 2];

      return {
        stopId: stop.stopId,
        arrivalTime: startDateTime.add(arrivalOffset, "minute").format("HH:mm"),
        departureTime: startDateTime
          .add(departureOffset, "minute")
          .format("HH:mm"),
      };
    });
  };

  const createStopSchedules = async (scheduleId, stopSchedules) => {
    if (!scheduleId || !stopSchedules.length) {
      return [];
    }

    const response = await axios.post(
      `${config.baseURL}/schedule/stop`,
      { scheduleId, stopSchedules },
      { withCredentials: true },
    );

    return response.data;
  };

  const createSchedule = async (routeId) => {
    try {
      // Validate required data
      if (!tripData.tripDate || !tripData.startTime || !tripData.endTime) {
        throw new Error("Missing trip date or time information");
      }

      // Combine date with start and end times
      const scheduleDate = tripData.tripDate;
      const startDateTime = scheduleDate
        .hour(tripData.startTime.hour())
        .minute(tripData.startTime.minute())
        .second(0);

      const endDateTime = scheduleDate
        .hour(tripData.endTime.hour())
        .minute(tripData.endTime.minute())
        .second(0);

      // Format schedule name as "SingleTrip_YYYY-MM-DD"
      const scheduleName = routeData.routeName;

      const payload = {
        name: scheduleName,
        repitation: "ONETIME",
        routeId: routeId,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        vehicleIds:
          routeData.selectedVehicles?.map((vehicle) => Number(vehicle.id)) || [],
        startDate: scheduleDate.startOf("day").toISOString(),
        endCondition: "NO_END",
        endDate: null,
        occurrences: null,
        repeatEvery: 1,
        weeklyDays: null,
        monthlyType: null,
        monthlyDay: null,
        monthlyWeekOrder: null,
        monthlyWeekDay: null,
      };

      const response = existingOneTimeSchedule?.id
        ? await axios.put(
            `${config.baseURL}/schedule`,
            {
              id: existingOneTimeSchedule.id,
              ...payload,
            },
            { withCredentials: true },
          )
        : await axios.post(`${config.baseURL}/schedule`, payload, {
            withCredentials: true,
          });

      const orderedStops = getOrderedRouteStops();
      const generatedStopSchedules = buildStopScheduleTimeline(
        orderedStops,
        startDateTime,
        endDateTime,
      );

      await createStopSchedules(response.data?.id, generatedStopSchedules);

      console.log("Schedule created successfully:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error creating schedule:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    let routeResponse;

    try {
      setSubmitError("");
      // First submit the route
      routeResponse = await onSubmit();

      console.log("Route response:", routeResponse);

      // Check if route was created/updated successfully
      if (!routeResponse || !routeResponse.id) {
        throw new Error("Route creation/update failed");
      }

      // If route type is Single Trip and we have trip data, create schedule
      if (
        routeType === "ONE_TIME_DELIVERY" &&
        tripData.tripDate &&
        tripData.startTime &&
        tripData.endTime
      ) {
        const routeId = routeResponse.id;

        if (routeId) {
          console.log("Creating schedule for route:", routeId);

          // Wait for schedule creation to complete
          await createSchedule(routeId);

          console.log("Route and schedule created successfully");
        } else {
          console.error("Missing required data:", { routeId });
          throw new Error("Missing routeId for schedule creation");
        }
      }

      // Navigate only after everything is complete
      navigate("/route");
    } catch (error) {
      console.error("Error in submit process:", error);
      const apiError = getApiErrorResponse(error);
      const baseMessage =
        apiError.code === "SCHEDULE_CONFLICT"
          ? buildScheduleConflictMessage(
              error,
              t("Failed to save route schedule"),
            )
          : getGenericApiErrorMessage(
              error,
              t("Failed to save route"),
            );

      setSubmitError(
        routeResponse?.id && apiError.code === "SCHEDULE_CONFLICT"
          ? `Route saved, but the schedule could not be created. ${baseMessage}`
          : baseMessage,
      );
    }
  };

  const handleRemoveStop = (stop) => {
    if (onStopSelect) {
      onStopSelect(stop);
    }
  };

  const handleRemoveIntermediatePoint = (point, index) => {
    if (onRouteDataChange) {
      const updatedRoutePoints = routeData.routePoints.filter(
        (p, i) => p.isStop || p.lat !== point.lat || p.lng !== point.lng
      );
      onRouteDataChange("routePoints", updatedRoutePoints);
    }
  };

  const isOriginDestinationSet = Boolean(
    routeData.origin && routeData.destination
  );

  return (
    <Box
      m={isSmallScreen ? 2 : 5}
      sx={{
        padding: isSmallScreen ? "15px" : "30px",
        borderRadius: 2,
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 3,
        }}
      >
        {/* Left Section - Route Details & Route Selection */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: isSmallScreen ? "100%" : "550px",
            width: isSmallScreen ? "100%" : "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Route Details */}
          <Box>
            {/* Header */}
            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontFamily: "Roboto",
                  fontSize: isSmallScreen ? "18px" : "20px",
                  lineHeight: "100%",
                  color: "#FFFFFF",
                  backgroundColor: "#647490",
                  padding: 2,
                  marginBottom: 2,
                  borderRadius: 4,
                  boxShadow: "-2px 2px 5px 0px #00000040",
                }}
              >
                {t("Basic Details")}
              </Typography>
            </Grid>
            <Grid container spacing={isSmallScreen ? 2 : 3}>
              {/* Route Name */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "16px", color: "#1F242E" }}>
                  {t("Route Name")} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Input
                  fullWidth
                  value={routeData.routeName}
                  placeholder="Type Route Name (e.g.Parking Bay)"
                  onChange={(e) =>
                    handleInputChange("routeName", e.target.value)
                  }
                  error={errors.routeName}
                  sx={{
                    mt: "8px",
                    height: "43px",
                    borderRadius: "16px",
                    borderWidth: 2,
                    padding: "16px 14px",
                    background: "#FFFFFF",
                    border: "2px solid #95A0B3",
                    fontSize: "13px",
                  }}
                />
                {errors.routeName && (
                  <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                    {t("Route Name is required")}
                  </Typography>
                )}
              </Grid>

              {/* Route ID */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "16px", color: "#1F242E" }}>
                  {t("Route ID")} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Input
                  fullWidth
                  value={routeData.routeId}
                  placeholder="Type Route ID (e.g. RT12345)"
                  onChange={(e) => handleInputChange("routeId", e.target.value)}
                  error={errors.routeId}
                  sx={{
                    mt: "8px",
                    height: "43px",
                    borderRadius: "14px",
                    borderWidth: 2,
                    padding: "16px 24px",
                    background: "#FFFFFF",
                    border: "2px solid #95A0B3",
                    fontSize: "13px",
                    "&::placeholder": {
                      fontSize: "16px",
                      color: "#C8CBD0",
                    },
                  }}
                />

                {errors.routeId && (
                  <Typography color="error" variant="caption" sx={{ pl: 1 }}>
                    {t("Route ID is required")}
                  </Typography>
                )}
              </Grid>

              {/* Route Color */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "16px", color: "#1F242E" }}>
                  {t("Select Route Colour")}
                </Typography>
                <Box
                  sx={{
                    mt: "8px",
                    width: "100%",
                    height: "43px",
                    border: "2px solid #95A0B3",
                    borderRadius: "16px",
                    padding: "16px 14px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "row",
                    cursor: "pointer",
                    gap: isSmallScreen ? 1 : 2,
                  }}
                  onClick={handleColorPickerOpen}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "16px",
                      lineHeight: "100%",
                      color: "#C8CBD0",
                      fontWeight: 500,
                      marginBottom: isExtraSmallScreen ? "10px" : 0,
                      fontSize: "13px",
                    }}
                  >
                    {t("Click to select color")}
                  </Typography>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "4px",
                      backgroundColor: routeData.routeColor || "#ADD8E6",
                      cursor: "pointer",
                      border: "2px solid #95A0B3",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Color Picker Popover */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleColorPickerClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ChromePicker
                color={routeData.routeColor}
                onChange={(color) => handleInputChange("routeColor", color.hex)}
              />
            </Popover>
          </Box>

          {/* Route Selection Component */}
          <Grid item xs={12}>
            <RouteSelection
              routeType={routeType}
              onRouteTypeChange={onRouteTypeChange}
              onRouteSchedulesClick={onRouteSchedulesClick}
              onAssignSchedulesClick={onAssignSchedulesClick}
              isUpdateMode={isUpdateMode}
              onTripDataChange={handleTripDataChange}
              initialTripData={initialTripData}
            />
          </Grid>
          <RouteAddMap
            stops={stops}
            selectedStops={selectedStops}
            onStopSelect={onStopSelect}
            allStopsData={stops}
            routeData={{
              ...routeData,
              onRouteDataChange: onRouteDataChange,
            }}
            onRouteDataChange={onRouteDataChange}
            errors={errors}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            width: isSmallScreen ? "100%" : "550px",
          }}
        >
          {/* Right Section - Stops */}
          <StopsSection
            stops={stops}
            selectedStops={selectedStops}
            routeData={routeData}
            onRouteDataChange={onRouteDataChange}
            onStopToggle={onStopSelect}
            onRemoveStop={handleRemoveStop}
            onRemoveIntermediatePoint={handleRemoveIntermediatePoint}
            errors={errors}
            isOriginDestinationSet={isOriginDestinationSet}
            routeType={routeType}
          />
          <VehiclesSection
            routeData={routeData}
            onRouteDataChange={onRouteDataChange}
            vehicles={vehicleList}
            // onVehicleChange={onVehicleSelect}
            onVehicleChange={(selectedVehicles) => {
              onRouteDataChange("selectedVehicles", selectedVehicles);
            }}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      {submitError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {submitError}
        </Alert>
      )}
      <SubmitButton
        isSmallScreen={isSmallScreen}
        isUpdateMode={isUpdateMode}
        onSubmit={handleSubmit}
      />
    </Box>
  );
}
